"use client";

import { RequestContext } from "asosteam";
import clsx from "clsx";
import { usePathname } from "next/navigation";
import { useRef, useState, useCallback, useEffect } from "react";
import type { NavigationResponse } from "@repo/apis/browse-navigation-v2";
import { Link } from "@repo/next-utils/router";
import * as Auth from "@repo/ui/auth";
import { Box } from "@repo/ui/box";
import { Button } from "@repo/ui/button";
import * as Container from "@repo/ui/container";
import * as Flex from "@repo/ui/flex";
import * as NavigationMenu from "@repo/ui/navigation-menu";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import { useFloorContext } from "@/components/floor-context/floor-context";
import { STRINGS } from "@/constants";
import { HamburgerMenuIcon } from "@/theme/icons/hamburger-menu";
import { HeartIcon } from "@/theme/icons/heart";
import { ProfileIcon } from "@/theme/icons/profile";
import { SearchIcon } from "@/theme/icons/search";
import { TYPOGRAPHY_LABEL3 } from "@/theme/topshop/typography/typography-constants";
import { useSearchDropdownState } from "@/utils/use-search-dropdown-state";
import { useImmersiveNav } from "../../utils/use-immersive-nav";
import { GlobalBanner } from "../global-banner";
import type { BannerCarouselContent } from "../global-banner/global-banner.types";
import { Logo } from "../logo";
import * as Viewport from "../viewport";
import { BagCounter } from "./bag-counter";
import { NavDrawer } from "./nav-drawer";
import { NavSearchDropdown, NavSearchDrawer } from "./nav-search";
import classes from "./nav.module.css";

type NavProps = {
  initialNavigation: NavigationResponse | null;
  requestContext: RequestContext;
  isContentNavTransparent?: boolean;
  isInverted?: boolean;
  bannerContent?: BannerCarouselContent;
};

const invertedPaths = ["/delivery-and-returns", "/bag", "/customer-care/"];

const isInvertedForPath = (pathname: string | null) => {
  const isExactlyCustomerCare = pathname?.endsWith("/customer-care");

  const key = invertedPaths.some((route) => {
    if (route === "/bag") {
      const segments = pathname?.split("/").filter(Boolean) || [];
      return segments[segments.length - 1] === "bag";
    }
    return pathname?.includes(route);
  });

  return key && !isExactlyCustomerCare;
};

export const Nav = ({
  initialNavigation,
  isContentNavTransparent = false,
  requestContext: {
    storeCode,
    lang,
    keyStoreDataversion,
    countryCode,
    currency,
    shop,
    storeUrls: { myAccount: accountUrl },
  },
  bannerContent: propBannerContent,
}: NavProps) => {
  const { status, authClient } = Auth.useAuth();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSearchSheetOpen, setIsSearchSheetOpen] = useState(false);
  const isSearchDropdownOpen = useSearchDropdownState();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const {
    isVisible,
    isImmersive,
    isHomepage,
    isSticky,
    onMouseEnter,
    onMouseLeave,
  } = useImmersiveNav({
    isDrawerOpen,
    isSearchDropdownOpen,
    isContentNavTransparent,
  });
  const { floor } = useFloorContext();
  const pathname = usePathname();

  const homepageHref = floor ? `/${floor}` : "/";
  const shouldShowGlobalBanner =
    propBannerContent !== undefined && propBannerContent.length > 0;

  useEffect(() => {
    if (shouldShowGlobalBanner) {
      document.documentElement.style.setProperty(
        "--sticky-offset",
        "calc(var(--nav-height) + var(--banner-height))",
      );
    } else {
      document.documentElement.style.setProperty(
        "--sticky-offset",
        "var(--nav-height)",
      );
    }

    if (shouldShowGlobalBanner && isContentNavTransparent) {
      document.documentElement.style.setProperty(
        "--hub-container-top-bottom-margin",
        "calc(var(--banner-height) + var(--nav-height)) 0",
      );
    } else {
      document.documentElement.style.removeProperty(
        "--hub-container-top-bottom-margin",
      );
    }
  }, [shouldShowGlobalBanner, isContentNavTransparent]);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
    setIsSearchSheetOpen(false);
    setTimeout(() => {
      menuButtonRef.current?.focus();
    }, 0);
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => !prev);
  }, []);

  return (
    <>
      {shouldShowGlobalBanner && propBannerContent && (
        <GlobalBanner content={propBannerContent} />
      )}
      <div
        className={clsx(
          classes.nav,
          !isVisible && classes.hidden,
          isInvertedForPath(pathname) && classes.inverted,
          isImmersive && classes.immersive,
          isHomepage && classes.fixed,
          classes.navHeader,
          shouldShowGlobalBanner && classes.withBanner,
          TYPOGRAPHY_LABEL3,
          isSticky && classes.sticky,
        )}
      >
        <Viewport.Large>
          <NavigationMenu.Root
            className={clsx(classes.nav, isImmersive && classes.immersive)}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <NavigationMenu.List
              orientation="horizontal"
              className={classes.list}
            >
              <Container.Root size="large" gutter="2xl">
                <Box paddingY="xs" asChild>
                  <Flex.Container justify="space-between" align="center">
                    <Flex.Container
                      className={classes.leftMenu}
                      gap="md"
                      align="center"
                    >
                      <Button
                        ref={menuButtonRef}
                        size="icon"
                        onClick={toggleDrawer}
                        className={classes.blendText}
                      >
                        <HamburgerMenuIcon
                          className={clsx(classes.icon, classes.blendText)}
                          label={STRINGS.nav.menu}
                        />
                      </Button>
                      <Flex.Container align="center" asChild>
                        <Link href={homepageHref}>
                          <Logo
                            variant={floor ?? "topshop"}
                            className={clsx(classes.logo, classes.blendText)}
                          />
                          <VisuallyHidden>{floor}</VisuallyHidden>
                        </Link>
                      </Flex.Container>
                    </Flex.Container>

                    <Flex.Container
                      className={classes.rightMenu}
                      align="center"
                      gap="md"
                    >
                      <div>
                        <NavigationMenu.Item>
                          {status === "authenticated" ? (
                            <a
                              href={accountUrl}
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.blendText,
                              )}
                            >
                              {authClient.getIdTokenClaims().firstName}
                            </a>
                          ) : (
                            <Auth.Login
                              className={clsx(
                                TYPOGRAPHY_LABEL3,
                                classes.blendText,
                              )}
                            >
                              {STRINGS.nav.loginSignUp}
                            </Auth.Login>
                          )}
                        </NavigationMenu.Item>
                      </div>
                      <div>
                        <NavSearchDropdown
                          store={storeCode}
                          lang={lang}
                          keyStoreDataversion={keyStoreDataversion}
                          country={countryCode}
                          currency={currency}
                          shop={shop}
                        />
                        <NavigationMenu.Item>
                          <Button asChild size="icon">
                            <Link href="/wishlist">
                              <HeartIcon
                                className={clsx(
                                  classes.icon,
                                  classes.blendText,
                                )}
                                label={STRINGS.nav.ariaLabels.goToWishlist}
                              />
                            </Link>
                          </Button>
                        </NavigationMenu.Item>
                        <NavigationMenu.Item>
                          <BagCounter
                            className={clsx(classes.icon, classes.blendText)}
                          />
                        </NavigationMenu.Item>
                      </div>
                    </Flex.Container>
                  </Flex.Container>
                </Box>
              </Container.Root>
            </NavigationMenu.List>
          </NavigationMenu.Root>
          <NavDrawer
            isOpen={isDrawerOpen}
            onClose={handleDrawerClose}
            initialNavigation={initialNavigation}
          />
        </Viewport.Large>

        <Viewport.Small>
          <Flex.Container asChild align="center">
            <nav
              className={classes.mobileNav}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              <Container.Root size="large" gutter="none">
                <Flex.Container justify="space-between" align="center">
                  <Flex.Container
                    className={classes.leftMenu}
                    align="center"
                    gap="md"
                  >
                    <Button
                      ref={menuButtonRef}
                      size="icon"
                      onClick={() => setIsDrawerOpen(true)}
                      className={classes.blendText}
                    >
                      <HamburgerMenuIcon
                        className={clsx(classes.icon, classes.blendText)}
                        label={STRINGS.nav.menu}
                      />
                    </Button>
                    <Link href={homepageHref}>
                      <Logo
                        variant={floor ?? "topshop"}
                        className={clsx(classes.mobileLogo, classes.blendText)}
                      />
                      <VisuallyHidden>{floor}</VisuallyHidden>
                    </Link>
                  </Flex.Container>
                  <Flex.Container
                    align="center"
                    gap="md"
                    className={classes.mobileRightIcons}
                  >
                    <Flex.Container align="center">
                      <Button size="icon" asChild>
                        <a href={accountUrl}>
                          <ProfileIcon
                            label={STRINGS.nav.ariaLabels.goToAccount}
                            className={clsx(classes.icon, classes.blendText)}
                          />
                        </a>
                      </Button>
                      <Button
                        size="icon"
                        onClick={() => setIsSearchSheetOpen(true)}
                      >
                        <SearchIcon
                          className={clsx(classes.icon, classes.blendText)}
                          label={STRINGS.nav.ariaLabels.searchMenu}
                        />
                      </Button>
                      <Button size="icon" asChild>
                        <Link href="/wishlist">
                          <HeartIcon
                            label={STRINGS.nav.ariaLabels.goToWishlist}
                            className={clsx(classes.icon, classes.blendText)}
                          />
                        </Link>
                      </Button>
                      <BagCounter
                        className={clsx(classes.icon, classes.blendText)}
                      />
                    </Flex.Container>
                  </Flex.Container>
                </Flex.Container>
              </Container.Root>
            </nav>
          </Flex.Container>

          <NavDrawer
            isOpen={isDrawerOpen}
            onClose={handleDrawerClose}
            initialNavigation={initialNavigation}
            country={countryCode}
          />
          <NavSearchDrawer
            isOpen={isSearchSheetOpen}
            onClose={handleDrawerClose}
            store={storeCode}
            lang={lang}
            keyStoreDataversion={keyStoreDataversion}
            country={countryCode}
            currency={currency}
            shop={shop}
          />
        </Viewport.Small>
      </div>
    </>
  );
};
